
"use client"

import React, { useState } from "react";
import { useGoogleLogin } from '@react-oauth/google';
import axios from "axios";
import { useRouter } from 'next/navigation';
import { v4 as uuidv4 } from 'uuid';

export default function SignupModal({ isOpen, onClose, isDark, showLoadingGoogleLOgin, setShowLoadingGoogleLogin }) {
    const router = useRouter();
    if (!isOpen) return null;
    const login = useGoogleLogin({
        onSuccess: async (tokenResponse) => {
            try {
                setShowLoadingGoogleLogin(true)
                const AUTH_URL = process.env.NEXT_PUBLIC_APP_GOOGLE_AUTH;
                const res = await axios.post(AUTH_URL, { access_token: tokenResponse.access_token, signup_from: "ai-website-builder" });

                localStorage.setItem('Exapirtoken', res.data.token);
                localStorage.setItem('userInfo', JSON.stringify(res.data));
                // Save token in localStorage or cookie
                localStorage.setItem("auth_token", res.data.token);

                document.cookie = "Exapirtoken=" + res.data.token + "; path=/; domain=.varnz.ai; secure; samesite=None";
                document.cookie = "userInfo=" + JSON.stringify(res.data) + "; path=/; domain=.varnz.ai; secure; samesite=None";
                const userDetails = {
                    email: res.data.data.email,
                    uid: res.data.data._id,
                    dynamicUserToken: res.data.token,
                    refCode: res.data.data.referral_code,
                    is_profile_complete: res.data.data.is_profile_complete,
                    gender: res.data.data.gender,
                    social_login: true,
                };
                const userKey = uuidv4();
                await axios.post(process.env.NEXT_PUBLIC_APP_GOOGLE_SIGNUP, { userId: userKey, accessToken: res.data.token, userInfo: res.data })

                const filteredUser = {
                    id: res.data.data._id,
                    token: res.data.token,
                    name: res.data.data?.name,
                    email: res.data.data.email,
                };

                try {
                    await axios.post(
                        `${process.env.NEXT_PUBLIC_APP_SITE_WEBSITE_URL}api/users/set-cookie`,
                        { user: filteredUser },
                        {
                            headers: {
                                "Content-Type": "application/json",
                                "x-auth-token": "",
                            },
                            withCredentials: true,
                        }
                    );
                } catch (cookieErr) {
                    console.warn("Cookie set failed:", cookieErr);
                }

                // router.push(`https://app.varnz.ai/login?loginWithGoogle=true&key=${userKey}&isWebsiteBuilder=true`)

                router.push(`/login?loginWithGoogle=true`)
            } catch (err) {
                console.error("❌ Error verifying Google token", err);
                toast.error(error?.response?.data?.message || "An error occurred while signing in.", {
                    style: { color: "red" },
                    position: "top-center",
                    iconTheme: { primary: "red", secondary: "#fff" }
                });
            } finally {
                setShowLoadingGoogleLogin(false)
            }
        },
        onError: () => console.error("❌ Google Login Failed"),
        flow: 'implicit'
    });

    return (
        <div
            id="signupModal"
            className={`fixed inset-0 z-[60] flex items-center justify-center p-6 ${isDark ? "bg-slate-900/80" : ""
                }`}
            aria-modal="true"
            role="dialog"
            aria-labelledby="signupModalTitle"
            aria-describedby="signupModalDesc"
        >
            <div
                className="absolute inset-0 bg-black/60"
                onClick={onClose}
                data-close
                aria-hidden="true"
            ></div>

            <div
                className={`relative max-w-md w-full rounded-2xl p-6 ring-1 shadow-sm
          ${isDark
                        ? "bg-slate-900 ring-white/10 text-slate-100"
                        : "bg-white ring-black/5 text-slate-900"
                    }`}
            >
                <button
                    className={`absolute right-3 top-3 h-9 w-9 rounded-full ring-1 hover:bg-slate-50 transition
            ${isDark
                            ? "ring-white/15 dark:hover:bg-white/5"
                            : "ring-slate-300/70"
                        }
          `}
                    onClick={onClose}
                    data-close
                    aria-label="Close"
                    type="button"
                >
                    ✕
                </button>

                <h3
                    id="signupModalTitle"
                    className="text-xl font-semibold mb-2"
                >
                    Create your Varnz account
                </h3>

                <p
                    id="signupModalDesc"
                    className={`${isDark ? "text-slate-300" : "text-slate-600"} mb-6 text-sm`}
                >
                    Please sign up to continue. You’ll unlock the AI Builder, saved projects, web page downloads, and personal & business cards.
                </p>

                <div className="space-y-3">
                    <a
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            const token = localStorage.getItem("auth_token");
                            if (token) {
                                router.push(`https://app.varnz.ai/builder`);
                            } else {
                                login();
                            }
                        }}
                        className={`w-full inline-flex items-center justify-center gap-3 rounded-full px-5 py-3 font-medium hover:opacity-90 transition
    ${isDark
                                ? "bg-white text-slate-900"
                                : "bg-slate-900 text-white"
                            }
  `}
                    >
                        {/* Google G SVG icon */}
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 48 48"
                            className="w-5 h-5"
                            fill="currentColor"
                        >
                            <path
                                d="M44.5 20H24v8.5h11.9C34.3 33.6 29.7 37 24 37c-7.2 0-13-5.8-13-13s5.8-13 13-13c3.3 0 6.3 1.3 8.4 3.5l6-6C34 8.6 29.4 7 24 7 13.5 7 5 15.5 5 26s8.5 19 19 19 19-8.5 19-19c0-1.3-.1-2.5-.5-3.5z"
                                fill="#FFC107"
                            />
                            <path
                                d="M6.7 14.9l6.6 4.8C14.4 17.1 18.8 14 24 14c3.3 0 6.3 1.3 8.4 3.5l6-6C34 8.6 29.4 7 24 7c-7.3 0-13.6 4-17.3 10z"
                                fill="#FF3D00"
                            />
                            <path
                                d="M24 43c5.4 0 10-1.8 13.4-5l-6.2-5c-2 1.4-4.6 2.2-7.2 2.2-5.7 0-10.3-4.1-11.9-9.7l-6.7 5.2C9.8 38.6 16.3 43 24 43z"
                                fill="#4CAF50"
                            />
                            <path
                                d="M43.5 20H24v8.5h11.9c-1.1 3-3.8 5.2-7 5.2-2.8 0-5.3-1.9-6.1-4.5H14v2.8c1.7 4.3 6 7.4 11 7.4 6.6 0 12-5.4 12-12 0-.8-.1-1.5-.5-2.2z"
                                fill="#1976D2"
                            />
                        </svg>

                        <span>{showLoadingGoogleLOgin ? "Please wait..." : "Continue with Google"}</span>
                    </a>

                    <a
                        href="/login"
                        className={`w-full inline-flex items-center justify-center rounded-full px-5 py-3 ring-1 font-medium transition hover:bg-indigo-50
                            ${isDark
                                ? "ring-white/15 text-slate-200 hover:bg-white/5"
                                : "ring-slate-300/70 text-slate-700"
                            }
                        `}
                        target="_blank"
                    >
                        <i className="fa-solid fa-envelope mr-2 text-lg"></i>
                        Continue with Email
                    </a>
                </div>

                <p className={`mt-4 flex justify-center text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                    By continuing, you agree to our{" "}&nbsp;
                    <a
                        href="/privacy-policy"
                        className="underline hover:text-indigo-500"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {" "} Terms & Privacy Policy.
                    </a>

                </p>
            </div>
        </div>
    );
}
